import * as cheerio from "cheerio";

import type { AnyNode } from "domhandler";

// --- unicode helpers (same as before) ---
const BOLD_MAP: Record<string, string> = {};
const ITALIC_MAP: Record<string, string> = {};

(function initMaps() {
	const normal = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";
	const bold = "𝐀𝐁𝐂𝐃𝐄𝐅𝐆𝐇𝐈𝐉𝐊𝐋𝐌𝐍𝐎𝐏𝐐𝐑𝐒𝐓𝐔𝐕𝐖𝐗𝐘𝐙" + "𝐚𝐛𝐜𝐝𝐞𝐟𝐠𝐡𝐢𝐣𝐤𝐥𝐦𝐧𝐨𝐩𝐪𝐫𝐬𝐭𝐮𝐯𝐰𝐱𝐲𝐳";
	const italic = "𝐴𝐵𝐶𝐷𝐸𝐹𝐺𝐻𝐼𝐽𝐾𝐿𝑀𝑁𝑂𝑃𝑄𝑅𝑆𝑇𝑈𝑉𝑊𝑋𝑌𝑍" + "𝑎𝑏𝑐𝑑𝑒𝑓𝑔ℎ𝑖𝑗𝑘𝑙𝑚𝑛𝑜𝑝𝑞𝑟𝑠𝑡𝑢𝑣𝑤𝑥𝑦𝑧";

	for (let i = 0; i < normal.length; i++) {
		BOLD_MAP[normal[i]] = bold[i] || normal[i];
		ITALIC_MAP[normal[i]] = italic[i] || normal[i];
	}
})();

function toUnicodeBold(text: string): string {
	return text
		.split("")
		.map((c) => BOLD_MAP[c] ?? c)
		.join("");
}

function toUnicodeItalic(text: string): string {
	return text
		.split("")
		.map((c) => ITALIC_MAP[c] ?? c)
		.join("");
}

// --- main function ---
export function convertHtmlToLinkedInText(html: string): string {
	if (!html) {
		return "";
	}

	const $ = cheerio.load(html);

	const out: string[] = [];

	function walkWithoutFormatting(node: AnyNode) {
		if (node.type === "text") {
			const text = (node.data || "").replace(/\s+/g, " ");

			out.push(text);

			return;
		}

		if (node.type !== "tag") {
			return;
		}

		const tag = node.name.toLowerCase();

		switch (tag) {
			case "br":
				out.push("\n");

				return;

			default:
				// Any other tag → just process children
				node.children?.forEach(walkWithoutFormatting);

				return;
		}
	}

	function walk(node: AnyNode) {
		// Text node
		if (node.type === "text") {
			const text = (node.data || "").replace(/\s+/g, " ");

			out.push(text);

			return;
		}

		if (node.type !== "tag") {
			return;
		}

		const tag = node.name.toLowerCase();

		switch (tag) {
			case "br":
				out.push("\n");

				return;

			case "p":
			case "div":
				node.children?.forEach(walk);
				out.push("\n\n");

				return;

			case "ul":
				node.children?.forEach((child) => {
					if (child.type === "tag" && child.name === "li") {
						out.push("• ");
						child.children?.forEach(walkWithoutFormatting);
						out.push("\n");
					}
				});

				return;

			case "ol": {
				let i = 1;

				node.children?.forEach((child) => {
					if (child.type === "tag" && child.name === "li") {
						out.push(`${i}. `);
						child.children?.forEach(walkWithoutFormatting);
						out.push("\n");
						i++;
					}
				});

				return;
			}

			case "li":
				// Usually handled by ul/ol, but fallback
				out.push("• ");
				node.children?.forEach(walk);
				out.push("\n");

				return;

			default:
				// Any other tag → just process children
				node.children?.forEach(walk);

				return;
		}
	}

	// Start walking from body-equivalent root
	$.root()
		.children()
		.each((_, el) => walk(el));

	let result = out.join("");

	// Cleanup
	result = result.replace(/\r/g, "");
	result = result.replace(/\n{3,}/g, "\n\n");
	result = result.replace(/[ \t]+\n/g, "\n");
	result = result.replace(/[ \t]{2,}/g, " ");

	return result.trim();
}
